import { GenericAbortSignal } from 'axios';
import { MentionItem } from 'react-mentions';

import { InfiniteData, useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';

import updateInfiniteQueryRecord from 'lib/updateInfiniteQueryRecord';
import ActivityModelFactory from 'models/activities/ActivityModelFactory';
import Activity, { ActivityOptions } from 'models/Activity';
import { Paginated } from 'models/Paginated';
import { updateActivity } from 'services/api/activity';
import http from 'services/http';
import { useToastActions } from 'stores/toastStore';

/** Its own key space, deliberately. `activityQueryKeys.index()` is an infinite query over the
 * patient story feed; sharing a prefix with it would mean a note posted here invalidates every
 * page of that. */
export const workflowActionActivityQueryKeys = {
  base: ['workflowActionActivities'],
  // Both ids, because both are in the request path — a key that named only the action would
  // let a cached thread be served under a different workflow without a refetch.
  thread: (workflowId: string, actionId: string) => ['workflowActionActivities', 'thread', workflowId, actionId],
};

/**
 * Whether this thread still holds a note the reader has never been shown, answered by the API on
 * every page it serves — after stamping that page read, so it already accounts for this fetch.
 *
 * Optional because it is additive: an api without it leaves the field off, and the caller falls
 * back to what it can see for itself. The two repos deploy in either order.
 */
export type ThreadMeta = { hasNewActivities?: boolean };

export type CreateWorkflowActionActivityParams = {
  text: string;
  plaintext?: string;
  mentions?: MentionItem[];
};

const threadPath = (workflowId: string, actionId: string) => `/workflows/${workflowId}/actions/${actionId}/activities`;

const toThread = (data: Paginated<Partial<ActivityOptions>, ThreadMeta>) => ({
  ...data,
  data: data.data.map((x) => ActivityModelFactory.get(x)),
});

export async function indexWorkflowActionActivities(
  workflowId: string,
  actionId: string,
  page?: number,
  signal?: GenericAbortSignal
) {
  return http
    .get<
      Paginated<Partial<ActivityOptions>, ThreadMeta>
    >(threadPath(workflowId, actionId), { params: { page }, signal })
    .then((res) => toThread(res.data));
}

export async function createWorkflowActionActivity(
  workflowId: string,
  actionId: string,
  params: CreateWorkflowActionActivityParams
) {
  return http
    .post<Paginated<Partial<ActivityOptions>, ThreadMeta>>(threadPath(workflowId, actionId), params)
    .then((res) => toThread(res.data));
}

/**
 * The tab loads the current thread every time it opens. Live updates land separately.
 *
 * Infinite, like the patient story feed (ActivityList.tsx:73): the endpoint reuses the shared
 * paginated index view, and with no Kaminari config in the API that means 25 per page. A plain
 * single-page query would drop note 26 onward with nothing on screen to say so.
 */
export const useWorkflowActionActivities = (workflowId: string, actionId: string, enabled = true) =>
  useInfiniteQuery({
    queryKey: workflowActionActivityQueryKeys.thread(workflowId, actionId),
    queryFn: ({ pageParam, signal }) => indexWorkflowActionActivities(workflowId, actionId, pageParam, signal),
    initialPageParam: 1,
    getNextPageParam: (lastPage, _pages, lastPageParam) =>
      lastPage.meta.totalPages > lastPageParam ? lastPageParam + 1 : undefined,
    enabled,
    refetchOnMount: 'always',
  });

/**
 * Create returns the thread's first page. Invalidate rather than write the response into the
 * cache: the cache is InfiniteData now, and a posted note shifts every subsequent page's
 * contents, so a partial write would leave later pages stale or duplicated.
 *
 * The error toast is not optional. http.ts sets `throwOnError: (error) => !isAxiosError(error)`,
 * so an axios failure never reaches an error boundary — without this the spinner just stops and
 * the note silently never posts. That includes the API's own guard against posting to a
 * superseded result, which comes back as a 422.
 */
export const useCreateWorkflowActionActivity = (workflowId: string, actionId: string) => {
  const queryClient = useQueryClient();
  const { addToast } = useToastActions();

  return useMutation({
    mutationFn: (params: CreateWorkflowActionActivityParams) =>
      createWorkflowActionActivity(workflowId, actionId, params),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: workflowActionActivityQueryKeys.thread(workflowId, actionId) });
    },
    onError: () => addToast({ text: 'Your note could not be posted. Please try again.' }),
  });
};

/**
 * Delete and restore both go through the top-level PUT /activities/:id — the API resolves a
 * workflow-action note through ActivityPolicy::Scope, so there is no thread-scoped route.
 *
 * Deliberately NOT useUpdateActivity: that hook's onSuccess writes activityQueryKeys.index()
 * and attachmentsQueryKeys.count(), neither of which matches a thread key, and branching it
 * would put the patient story feed at risk for no gain here.
 */
export const useArchiveWorkflowActionActivity = (workflowId: string, actionId: string) => {
  const queryClient = useQueryClient();
  const { addToast } = useToastActions();

  return useMutation({
    mutationFn: ({ id, archived }: { id: string; archived: boolean }) => updateActivity({ id, archived }),
    onSuccess: (updated) => {
      // Archiving swaps one note in place — it neither adds nor removes rows, so the pages stay
      // aligned and the record can be patched without a refetch.
      queryClient.setQueryData<InfiniteData<Paginated<Activity>>>(
        workflowActionActivityQueryKeys.thread(workflowId, actionId),
        (prev) => updateInfiniteQueryRecord(prev, updated)
      );
    },
    // Same silent-failure trap as create: the row simply stays as it was, with the menu closed
    // behind it. Worded for both directions, since one mutation serves delete and undo.
    onError: (_error, { archived }) =>
      addToast({ text: `Your note could not be ${archived ? 'deleted' : 'restored'}. Please try again.` }),
  });
};
